import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  LayoutAnimation,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  UIManager,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";

import { useAuth } from "../../../context/AuthContext";
import { AuthStackParamList } from "../../navigation/navigationTypes";
import { UserService } from "../../services/UserService";
import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import { DivineCareScreen } from "./DivineCareScreen";
import {
  isValidHistoryDate,
} from "../../shared/utils/historyNavigation";

if (Platform.OS === "android") {
  UIManager.setLayoutAnimationEnabledExperimental?.(true);
}

type TrackerTab = "track" | "divine";

type SadhanaTrackerRouteProp = RouteProp<AuthStackParamList, "SadhanaTracker">;
type TrackerSelectedDevotee = NonNullable<AuthStackParamList["SadhanaTracker"]>["selectedDevotee"];
type HistoryRouteName =
  | "DandvatsHistory"
  | "ChantingHistory"
  | "SringarHistory"
  | "ManglaAartiHistory"
  | "ReadingHistoryScreen"
  | "HearingHistory"
  | "DeityWorshipHistory"
  | "UpGradationHistory"
  | "PranayamHistory"
  | "DietChartHistory"
  | "ExerciseHistory"
  | "OtherSevaHistory";

const CATEGORY_ROUTE_MAP: Record<string, HistoryRouteName> = {
  dandvat: "DandvatsHistory",
  dandvats: "DandvatsHistory",
  chanting: "ChantingHistory",
  "sringar gurupuja": "SringarHistory",
  "mangla aarti": "ManglaAartiHistory",
  reading: "ReadingHistoryScreen",
  hearing: "HearingHistory",
  deity: "DeityWorshipHistory",
  "deity worship": "DeityWorshipHistory",
  upgradation: "UpGradationHistory",
  "up gradation": "UpGradationHistory",
  "up-gradation": "UpGradationHistory",
  pranayam: "PranayamHistory",
  diet: "DietChartHistory",
  exercise: "ExerciseHistory",
  other: "OtherSevaHistory"
};

const normalizeCategoryKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .trim();

const maskMobile = (num: string) => {
  if (!num) return "";
  return "XXXXXXXX" + num.slice(-2);
};

type TrackDevoteesProps = {
  isGuru: boolean;
  currentUserId: string;
  isActive: boolean;
  notificationDate?: string | null;
  initialSelectedDevotee?: {
    userId?: string;
    name?: string | null;
  } | null;
};

function TrackDevoteesTab({
  isGuru,
  currentUserId,
  isActive,
  notificationDate,
  initialSelectedDevotee,
}: TrackDevoteesProps) {
  const navigation = useNavigation<any>();
  const resolvedInitialDate = isValidHistoryDate(notificationDate)
    ? String(notificationDate).trim()
    : null;
  const initialSelectedUserId = initialSelectedDevotee?.userId?.trim() || "";
  const [devotees, setDevotees] = useState<any[]>([]);
  const [selectedDevotees, setSelectedDevotees] = useState<string[]>(
    isGuru
      ? initialSelectedUserId
        ? [initialSelectedUserId]
        : []
      : currentUserId
        ? [currentUserId]
        : []
  );
  const [devoteeScores, setDevoteeScores] = useState<any[]>([]);
  const [filterType, setFilterType] = useState(
    resolvedInitialDate ? "Custom" : "Weekly"
  );
  const [showFilter, setShowFilter] = useState(true);
  const [startDate, setStartDate] = useState(
    resolvedInitialDate ?? format(startOfWeek(new Date()), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(
    resolvedInitialDate ?? format(endOfWeek(new Date()), "yyyy-MM-dd")
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    devotees: 0,
    avgMarks: 0,
    topScore: 0,
  });
  const scoreRequestIdRef = useRef(0);

  const loadDevotees = useCallback(async () => {
    const res = await UserService.getDevoteesList();
    if (res?.data) {
      setDevotees(res.data);
    }
  }, []);

  const loadScores = useCallback(async () => {
    const requestId = scoreRequestIdRef.current + 1;
    scoreRequestIdRef.current = requestId;
    const payload = {
      fromDate: startDate,
      toDate: endDate,
      devotees: isGuru ? selectedDevotees : currentUserId ? [currentUserId] : [],
    };

    const res = await UserService.getDevoteesProgress(payload);
    if (requestId !== scoreRequestIdRef.current) {
      return;
    }

    if (res?.data) {
      setSummary({
        devotees: res.data.devotees,
        avgMarks: res.data.avgMarks,
        topScore: res.data.topScore,
      });
      setDevoteeScores(res.data.devotee);
    }
  }, [currentUserId, endDate, isGuru, selectedDevotees, startDate]);

  useEffect(() => {
    if (isGuru) {
      loadDevotees();
      return;
    }

    setShowDropdown(false);
    setSearch("");
    setDevotees([]);
    setSelectedDevotees(currentUserId ? [currentUserId] : []);
  }, [currentUserId, isGuru, loadDevotees]);

  useEffect(() => {
    if (!isGuru) {
      return;
    }

    if (!isActive) {
      setSelectedDevotees([]);
      setSearch("");
      setShowDropdown(false);
      return;
    }

    const nextSelectedUserId = initialSelectedDevotee?.userId?.trim() || "";
    setSelectedDevotees(nextSelectedUserId ? [nextSelectedUserId] : []);
    setSearch("");
    setShowDropdown(false);
  }, [initialSelectedDevotee?.userId, isActive, isGuru]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  useEffect(() => {
    if (!resolvedInitialDate || !isActive) {
      return;
    }

    setFilterType("Custom");
    setStartDate(resolvedInitialDate);
    setEndDate(resolvedInitialDate);
    setShowFilter(true);
  }, [isActive, resolvedInitialDate]);

  const toggleDevotee = (id: string) => {
    let updated = [...selectedDevotees];
    if (updated.includes(id)) updated = updated.filter((value) => value !== id);
    else updated.push(id);
    setSelectedDevotees(updated);
  };

  const filteredDevotees = useMemo(
    () =>
      devotees.filter(
        (item) =>
          item.fullName.toLowerCase().includes(search.toLowerCase()) ||
          item.mobileNumber.includes(search)
      ),
    [devotees, search]
  );

  const openDatePicker = (type: "start" | "end") => {
    DateTimePickerAndroid.open({
      value: new Date(type === "start" ? startDate : endDate),
      mode: "date",
      onChange: (_, date) => {
        if (!date) return;
        const formatted = format(date, "yyyy-MM-dd");
        if (type === "start") setStartDate(formatted);
        else setEndDate(formatted);
      },
    });
  };

  const handleFilterTypeChange = (type: string) => {
    setFilterType(type);
    const today = new Date();
    let start = today;
    let end = today;

    switch (type) {
      case "Weekly":
        start = startOfWeek(today);
        end = endOfWeek(today);
        break;
      case "Monthly":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "Quarterly":
        start = startOfQuarter(today);
        end = endOfQuarter(today);
        break;
      case "Yearly":
        start = startOfYear(today);
        end = endOfYear(today);
        break;
      default:
        break;
    }

    setStartDate(format(start, "yyyy-MM-dd"));
    setEndDate(format(end, "yyyy-MM-dd"));
  };

  const toggleExpand = (id: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpanded(expanded === id ? null : id);
  };

  const handleCategoryPress = (categoryType?: string, devoteeId?: string) => {
    const key = normalizeCategoryKey(String(categoryType ?? ""));
    const routeName = CATEGORY_ROUTE_MAP[key];

    if (!routeName) return;

    navigation.navigate(routeName as never, {
      fromDate: startDate || undefined,
      toDate: endDate || undefined,
      devoteeId: devoteeId || undefined,
    } as never);
  };

  const renderDropdownItem = useCallback(
    ({ item }: { item: any }) => (
      <TouchableOpacity
        key={item.userId}
        style={styles.dropdownItem}
        onPress={() => toggleDevotee(item.userId)}
      >
        <Icon
          name={selectedDevotees.includes(item.userId) ? "checkbox-marked" : "checkbox-blank-outline"}
          size={20}
          color="#f97316"
        />
        <View style={styles.dropdownMeta}>
          <Text style={styles.dropdownName}>{item.fullName}</Text>
          <Text style={styles.dropdownMobile}>{maskMobile(item.mobileNumber)}</Text>
        </View>
      </TouchableOpacity>
    ),
    [selectedDevotees]
  );

  const renderHeader = () => (
    <>
      <View style={styles.summaryRow}>
        <View style={styles.summaryCard}>
          <View style={styles.summaryHeader}>
            <Icon name="account-group" size={16} color="#c2410c" />
            <Text style={styles.summaryLabel}>Devotees</Text>
          </View>
          <Text style={styles.summaryValue}>{summary.devotees}</Text>
        </View>

        <View style={styles.summaryCardGreen}>
          <View style={styles.summaryHeader}>
            <Icon name="trending-up" size={16} color="#059669" />
            <Text style={styles.summaryLabel}>Avg Marks</Text>
          </View>
          <Text style={styles.summaryValueGreen}>{summary.avgMarks}</Text>
        </View>

        <View style={styles.summaryCardPurple}>
          <View style={styles.summaryHeader}>
            <Icon name="trophy" size={16} color="#7c3aed" />
            <Text style={styles.summaryLabel}>Top Score</Text>
          </View>
          <Text style={styles.summaryValuePurple}>{summary.topScore}</Text>
        </View>
      </View>

      <View style={styles.filterBox}>
        <TouchableOpacity style={styles.filterHeaderRow} onPress={() => setShowFilter(!showFilter)}>
          <View style={styles.filterLeft}>
            <Icon name="filter-variant" size={22} color="#f97316" />
            <Text style={styles.filterTitle}>Filter Sadhana</Text>
          </View>
          <Icon name={showFilter ? "chevron-down" : "chevron-right"} size={24} color="#444" />
        </TouchableOpacity>

        {showFilter && (
          <>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.filterRow}>
                {["Weekly", "Monthly", "Quarterly", "Yearly"].map((type) => (
                  <TouchableOpacity
                    key={type}
                    onPress={() => handleFilterTypeChange(type)}
                    style={[styles.filterBtn, filterType === type && styles.filterActive]}
                  >
                    <Text style={[styles.filterText, filterType === type && styles.filterTextActive]}>
                      {type}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>

            <View style={styles.dateRow}>
              <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("start")}>
                <Icon name="calendar" size={18} color="#d04040" />
                <Text style={styles.dateText}>{startDate}</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("end")}>
                <Icon name="calendar" size={18} color="#d04040" />
                <Text style={styles.dateText}>{endDate}</Text>
              </TouchableOpacity>
            </View>

            {isGuru && (
              <>
                <Text style={styles.label}>Select Devotees</Text>

                <TouchableOpacity style={styles.dropdown} onPress={() => setShowDropdown(!showDropdown)}>
                  <Text>
                    {selectedDevotees.length === 0 ? "Select devotees..." : `${selectedDevotees.length} selected`}
                  </Text>
                  <Icon name="chevron-down" size={20} />
                </TouchableOpacity>

                {showDropdown && (
                  <KeyboardAvoidingView
                    behavior={Platform.OS === "ios" ? "padding" : "height"}
                    keyboardVerticalOffset={Platform.OS === "ios" ? 110 : 90}
                  >
                    <View style={[styles.dropdownBox, styles.dropdownBoxSafe]}>
                      <View style={styles.searchWrap}>
                        <Icon name="magnify" size={18} color="#9CA3AF" />
                        <TextInput
                          placeholder="Search devotees by name"
                          placeholderTextColor="#9CA3AF"
                          value={search}
                          onChangeText={setSearch}
                          style={styles.search}
                          autoCorrect={false}
                          autoCapitalize="none"
                          returnKeyType="search"
                        />
                      </View>

                      <FlatList
                        data={filteredDevotees}
                        keyExtractor={(item) => item.userId}
                        renderItem={renderDropdownItem}
                        style={styles.dropdownList}
                        keyboardShouldPersistTaps="handled"
                        nestedScrollEnabled
                      />

                      <View style={styles.dropdownFooter}>
                        <TouchableOpacity onPress={() => setSelectedDevotees(devotees.map((item) => item.userId))}>
                          <Text style={styles.footerBtn}>Select All</Text>
                        </TouchableOpacity>

                        <TouchableOpacity onPress={() => setSelectedDevotees([])}>
                          <Text style={styles.footerBtn}>Clear All</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </KeyboardAvoidingView>
                )}
              </>
            )}
          </>
        )}
      </View>
    </>
  );

  const renderDevotee = ({ item }: any) => (
    <View style={styles.card}>
      <TouchableOpacity style={styles.cardHeader} onPress={() => toggleExpand(item.mobileNo)}>
        <View style={styles.rank}>
          <Text style={styles.rankText}>#{item.rank}</Text>
        </View>

        <View style={styles.flexOne}>
          <Text style={styles.name}>{item.fullName}</Text>
          <Text style={styles.mobile}>{maskMobile(item.mobileNo)}</Text>
        </View>

        <View style={styles.scoreBox}>
          <Text style={styles.score}>{item.totalScore}</Text>
          <Text style={styles.total}>Total Marks</Text>
        </View>

        <Icon
          name={expanded === item.mobileNo ? "chevron-down" : "chevron-right"}
          size={22}
          color="#999"
        />
      </TouchableOpacity>

      {expanded === item.mobileNo && (
        <View style={styles.breakdown}>
          {item.category.map((category: any) => (
            <Pressable
              style={styles.breakItem}
              key={category.type}
              onPress={() => handleCategoryPress(category.type, item.devoteeId)}
            >
              <View style={styles.breakLeft}>
                <Text>{category.type}</Text>
                <Icon name="chevron-right" size={18} color="#9CA3AF" />
              </View>
              <Text style={styles.breakScore}>{category.score}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.listWrap}>
      {renderHeader()}
      <FlatList
        data={devoteeScores}
        keyExtractor={(item) => item.mobileNo}
        renderItem={renderDevotee}
        contentContainerStyle={[
          styles.scoreListContent,
          showDropdown ? styles.scoreListContentWithDropdown : null,
        ]}
        keyboardShouldPersistTaps="handled"
      />
    </View>
  );
}

export default function SadhanaTrackerScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<SadhanaTrackerRouteProp>();
  const { user } = useAuth();
  const isGuru = user?.userType === "Guru";
  const requestedTab = route.params?.initialTab;
  const [trackTabResetKey, setTrackTabResetKey] = useState(0);
  const [pendingSelectedDevotee, setPendingSelectedDevotee] = useState<TrackerSelectedDevotee | null>(
    route.params?.selectedDevotee ?? null
  );
  const notificationDate = isValidHistoryDate(route.params?.notificationDate)
    ? String(route.params?.notificationDate).trim()
    : null;
  const [activeTab, setActiveTab] = useState<TrackerTab>(
    requestedTab === "divine" ? "divine" : "track"
  );

  useEffect(() => {
    if (requestedTab === "divine" || requestedTab === "track") {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  useEffect(() => {
    if (route.params?.selectedDevotee?.userId && requestedTab === "track") {
      setPendingSelectedDevotee(route.params.selectedDevotee);
    }
  }, [requestedTab, route.params?.selectedDevotee]);

  useEffect(() => {
    if (notificationDate && requestedTab === "track") {
      setTrackTabResetKey((current) => current + 1);
    }
  }, [notificationDate, requestedTab]);

  const clearTemporarySelection = useCallback(() => {
    setPendingSelectedDevotee(null);
    setTrackTabResetKey((current) => current + 1);
    navigation.setParams({
      initialTab: undefined,
      notificationDate: undefined,
      selectedDevotee: undefined,
    });
  }, [navigation]);

  useEffect(() => {
    if (activeTab === "divine") {
      clearTemporarySelection();
    }
  }, [activeTab, clearTemporarySelection]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        clearTemporarySelection();
      };
    }, [clearTemporarySelection])
  );

  return (
    <DivineBackground>
      <Header title="Sadhana Tracker" showProfile showBack />

      <View style={styles.screenBody}>
        <View style={styles.tabBar}>
          <Pressable
            onPress={() => setActiveTab("track")}
            style={[styles.tabButton, activeTab === "track" && styles.tabButtonActive]}
          >
            <Icon
              name="lightning-bolt-outline"
              size={15}
              color={activeTab === "track" ? "#FFFFFF" : "#92400E"}
            />
            <Text style={[styles.tabButtonText, activeTab === "track" && styles.tabButtonTextActive]}>
              Track Devotees
            </Text>
          </Pressable>

          <Pressable
            onPress={() => setActiveTab("divine")}
            style={[styles.tabButton, activeTab === "divine" && styles.divineTabButtonActive]}
          >
            <Icon
              name="bell-outline"
              size={15}
              color={activeTab === "divine" ? "#FFFFFF" : "#7F1D1D"}
            />
            <Text style={[styles.tabButtonText, activeTab === "divine" && styles.tabButtonTextActive]}>
              Divine Care
            </Text>
          </Pressable>
        </View>

        {activeTab === "track" ? (
          <TrackDevoteesTab
            key={trackTabResetKey}
            isGuru={isGuru}
            currentUserId={user?.userId ?? ""}
            isActive={activeTab === "track"}
            notificationDate={notificationDate}
            initialSelectedDevotee={pendingSelectedDevotee ?? null}
          />
        ) : (
          <DivineCareScreen
            visible={activeTab === "divine"}
            selectedDevotee={null}
          />
        )}
      </View>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  screenBody: { flex: 1 },
  tabBar: {
    flexDirection: "row",
    gap: 10,
    paddingHorizontal: 14,
    paddingTop: 14,
  },
  tabButton: {
    flex: 1,
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: "rgba(255,247,237,0.98)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  tabButtonActive: {
    backgroundColor: "#EA580C",
    borderColor: "#EA580C",
  },
  divineTabButtonActive: {
    backgroundColor: "#E11D48",
    borderColor: "#E11D48",
  },
  tabButtonText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#92400E",
  },
  tabButtonTextActive: { color: "#FFFFFF" },
  listContent: {
    paddingHorizontal: 14,
    paddingBottom: 20,
    paddingTop: 14,
  },
  listWrap: {
    flex: 1,
    paddingHorizontal: 14,
    paddingBottom: 20,
    paddingTop: 14,
  },
  scoreListContent: {
    paddingTop: 14,
    paddingBottom: 12,
  },
  scoreListContentWithDropdown: {
    paddingBottom: 120,
  },
  summaryRow: { flexDirection: "row", gap: 10 },
  summaryHeader: { flexDirection: "row", alignItems: "center", gap: 6 },
  summaryCard: { flex: 1, backgroundColor: "#f5eee6", padding: 14, borderRadius: 12 },
  summaryCardGreen: { flex: 1, backgroundColor: "#e8f5ee", padding: 14, borderRadius: 12 },
  summaryCardPurple: { flex: 1, backgroundColor: "#eee6f7", padding: 14, borderRadius: 12 },
  summaryLabel: { fontSize: 12, color: "#777" },
  summaryValue: { fontSize: 22, fontWeight: "bold", color: "#d97706" },
  summaryValueGreen: { fontSize: 22, fontWeight: "bold", color: "#059669" },
  summaryValuePurple: { fontSize: 22, fontWeight: "bold", color: "#7c3aed" },
  filterBox: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 14,
    marginTop: 15,
  },
  filterHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  filterLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  filterTitle: { fontWeight: "700", fontSize: 16 },
  filterRow: { flexDirection: "row", gap: 10 },
  filterBtn: {
    backgroundColor: "#fde8d8",
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
  },
  filterActive: { backgroundColor: "#f97316" },
  filterText: { color: "#f97316" },
  filterTextActive: { color: "#fff" },
  dateRow: { flexDirection: "row", gap: 10, marginTop: 10 },
  dateInput: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#ddd",
    padding: 10,
    borderRadius: 8,
  },
  dateText: { color: "#555" },
  label: { marginTop: 10, marginBottom: 5, fontWeight: "600" },
  dropdown: {
    borderWidth: 1,
    borderColor: "#ddd",
    padding: 12,
    borderRadius: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dropdownBox: {
    borderWidth: 1,
    borderColor: "#ddd",
    marginTop: 5,
    borderRadius: 8,
    backgroundColor: "#fff",
    overflow: "hidden",
    maxHeight: 240,
  },
  dropdownBoxSafe: {
    marginBottom: 96,
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderBottomWidth: 1,
    borderColor: "#eee",
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: "#FFFBF7",
    flexShrink: 0,
  },
  search: {
    flex: 1,
    minHeight: 40,
    fontSize: 13,
    color: "#374151",
    paddingVertical: 0,
  },
  dropdownList: {
    maxHeight: 120,
    flexGrow: 0,
  },
  dropdownItem: { flexDirection: "row", alignItems: "center", gap: 10, padding: 10 },
  dropdownMeta: { flex: 1 },
  dropdownName: { fontWeight: "600" },
  dropdownMobile: { color: "#777" },
  dropdownFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    backgroundColor: "#fff",
    flexShrink: 0,
  },
  footerBtn: { color: "#f97316", fontWeight: "600" },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    marginTop: 14,
    paddingVertical: 4,
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  cardHeader: { flexDirection: "row", alignItems: "center", padding: 14 },
  rank: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#f97316",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  rankText: { color: "#fff", fontWeight: "bold" },
  flexOne: { flex: 1 },
  name: { fontWeight: "bold" },
  mobile: { fontSize: 12, color: "#888" },
  scoreBox: { alignItems: "flex-end", marginRight: 10 },
  score: { fontSize: 22, fontWeight: "bold", color: "#f97316" },
  total: { fontSize: 12, color: "#888" },
  breakdown: { backgroundColor: "#fff7ed", padding: 10 },
  breakItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderRadius: 6,
    marginBottom: 5,
  },
  breakLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  breakScore: { fontWeight: "bold" },
});
